import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useSelectedPage } from "../../../../Hooks/Display/usePages";
import { useSelectedComponent } from "../../../../Hooks/Display/useComponents";
import { useMemo, useState } from "react";
import { ATTRIBUTE_LABEL, ATTRIBUTE_PLURAL_LABEL } from "../../../Helpers/Labels/AttributeLabel";
import AttributeTypes from "../../../Attributes/AttributeTypes";
import { useTranslation } from "react-i18next";

function SelectAttributesDialog({ attributes, componentAttributes, onSelect, onCancel }) {

    const { t } = useTranslation([
        "attribute/attribute_type",
        "display/tables/attribute"
    ], { useSuspense: false })

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const dialogTitle = useMemo(() => {
        return selectedComponent.componentType === "Calendar"
            ? t("select_attributes_dialog.calendar_component_title", { ns: "display/tables/attribute" })
            : t("select_attributes_dialog.title", { ns: "display/tables/attribute" })
    }, [selectedPage, selectedComponent]);

    const [selectedAttributes, setSelectedAttributes] = useState(componentAttributes);

    function isAttributeSelected(id) {
        return selectedAttributes.some(attribute => attribute.attributeId === id);
    }

    function selectAttribute(id) {
        setSelectedAttributes([
            ...selectedAttributes,
            {
                attributeId: id,
                hyperlink: false,
                group: "",
                tooltip: false,
                cellRules: false,
                targetPageId: ""
            }
        ]);
    }

    function deselectAttribute(id) {
        setSelectedAttributes(selectedAttributes.filter(attribute => attribute.attributeId !== id));
    }

    function handleCheck(event, attributeId) {
        if (event.target.checked) {
            selectAttribute(attributeId);
        } else {
            deselectAttribute(attributeId);
        }
    }

    function handleSelect() {
        onSelect(selectedAttributes);
    }

    function handleSelectAll() {
        if (selectedAttributes.length === attributes.length) {
            setSelectedAttributes([]);
        } else {
            setSelectedAttributes(attributes.map(attribute => ({
                attributeId: attribute._id,
                hyperlink: false,
                tooltip: false,
                targetPageId: ""
            })));
        }
    }

    function getDisplayType(type) {
        const translatedTypes = AttributeTypes.getTranslatedTypes(t);
        const typeObj = translatedTypes.find(t => t.value === type);
        if (!typeObj && type === "Entity Lookup") {
            return t("attribute_type_label.entity_lookup", { ns: "attribute/attribute_type" });
        }
        return typeObj ? typeObj.text : "Type not found";
    }

    return (
        <Dialog
            title={dialogTitle}
            height={500}
            width={"70%"}
            onClose={onCancel}
        >
            <div className="m-3">
                <Grid data={attributes}>
                    <GridColumn
                        field="attributeLabel"
                        title={t("select_attributes_dialog.grid.column_name", { ns: "display/tables/attribute" })}
                    />
                    <GridColumn
                        title="Type"
                        cell={({ dataItem }) => (
                            <td>
                                {getDisplayType(dataItem.attributeType)}
                            </td>
                        )}
                    />
                    <GridColumn
                        field="" title="Select"
                        headerCell={() => (
                            (selectedComponent.componentType === "Calendar" ?
                                null :
                                <Checkbox
                                    onClick={handleSelectAll}
                                    label={t("select_attributes_dialog.grid.select_all", { ns: "display/tables/attribute" })}
                                    labelPlacement="after"
                                    className="checkbox"
                                    disabled={selectedComponent.componentType === "Calendar" && selectedAttributes.length >= 4}
                                    checked={selectedAttributes.length === attributes.length}
                                    indeterminate={selectedAttributes.length > 0 && selectedAttributes.length < attributes.length}
                                />
                            )
                        )}
                        cell={({ dataItem }) => (
                            <td>
                                <Checkbox
                                    onClick={(event) => handleCheck(event, dataItem._id)}
                                    disabled={
                                        (selectedPage.subPageType === "New Record" && dataItem.required === true) ||
                                        (
                                            selectedComponent.componentType === "Calendar" &&
                                            selectedAttributes.length >= 4 &&
                                            !isAttributeSelected(dataItem._id)
                                        )
                                    }
                                    checked={
                                        selectedComponent.componentType === "Form" && selectedPage.subPageType === "New Record"
                                            ? dataItem.required === true || isAttributeSelected(dataItem._id)
                                            : isAttributeSelected(dataItem._id)
                                    }
                                />
                            </td>
                        )}
                    />
                </Grid>
            </div>
            <DialogActionsBar layout="end">
                <div className="m-2 me-4 mb-3">
                    {
                        selectedAttributes.length === 0 &&
                        <span className="error">
                            {t("select_attributes_dialog.errors.select_attributes", { ns: "display/tables/attribute" })}
                        </span>
                    }
                    <button className="btn" onClick={onCancel}>
                        {t("select_attributes_dialog.buttons.cancel", { ns: "display/tables/attribute" })}
                    </button>
                    <button className="btn btn-primary" onClick={handleSelect} disabled={selectedAttributes.length === 0}>
                        {t("select_attributes_dialog.buttons.select", { ns: "display/tables/attribute" })}
                    </button>
                </div>
            </DialogActionsBar>
        </Dialog>
    )
}

export default SelectAttributesDialog;